import React, { useState, useEffect } from 'react';
import { List, Typography, Alert, Tag, Space, Skeleton } from 'antd';
import { ExclamationCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  getBulletinBoard,
  formatNifiBulletinTimestamp,
  nifiBulletinTimestampToMs,
} from './utils/nifiHelper';

const { Text, Paragraph } = Typography;

const Bulletins = ({ onRefresh, onDataChange, userInfo }) => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const nifiUsername = userInfo?.nifiUsername;
      const nifiPassword = userInfo?.nifiPassword;
      
      if (!nifiUsername || !nifiPassword) {
        throw new Error('NiFi credentials not configured');
      }
      
      const bulletinData = await getBulletinBoard(nifiUsername, nifiPassword);

      const bulletinList = bulletinData?.bulletinBoard?.bulletins || [];
      const sortedBulletins = bulletinList.sort(
        (a, b) => nifiBulletinTimestampToMs(b.bulletin?.timestamp) - nifiBulletinTimestampToMs(a.bulletin?.timestamp)
      );
      setBulletins(sortedBulletins);
      
      if (onDataChange) {
        onDataChange(sortedBulletins.length > 0);
      }
    } catch (err) {
      setError(`Failed to fetch bulletins: ${err.message}`);
      console.error('Error fetching bulletins:', err);
      if (onDataChange) {
        onDataChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getBulletinIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warn':
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'info':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getBulletinColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return 'error';
      case 'warn':
      case 'warning':
        return 'warning';
      case 'info':
        return 'processing';
      default:
        return 'default';
    }
  };

  const errorCount = bulletins.filter(b => b.bulletin?.level?.toLowerCase() === 'error').length;
  const warningCount = bulletins.filter(b => b.bulletin?.level?.toLowerCase() === 'warn' || b.bulletin?.level?.toLowerCase() === 'warning').length;

  if (loading) {
    return (
      <div style={{ 
        padding: '12px', 
        height: '400px', 
        width: '100%'
      }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Skeleton active title={{ width: '35%' }} paragraph={{ rows: 1, width: ['55%'] }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '12px', height: '400px' }}>
        <Alert
          message="Error Loading Bulletins"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="medium" style={{ width: '100%' }}>
      <Space>
        <Tag color="error">{errorCount} Errors</Tag>
        <Tag color="warning">{warningCount} Warnings</Tag>
        <Tag color="default">{bulletins.length} Total</Tag>
      </Space>
      
      <div style={{ 
        height: '400px', 
        overflowY: 'auto',
        padding: '8px'
      }}>
        {bulletins.length > 0 ? (
          <List
            itemLayout="vertical"
            dataSource={bulletins}
            renderItem={(item) => {
              const bulletin = item.bulletin || {};
              return (
                <List.Item 
                  style={{ 
                    padding: '12px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      {getBulletinIcon(bulletin.level)}
                      <Tag color={getBulletinColor(bulletin.level)}>
                        {bulletin.level?.toUpperCase() || 'INFO'}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {formatNifiBulletinTimestamp(bulletin.timestamp)}
                      </Text>
                    </Space>
                    
                    {bulletin.sourceId && (
                      <Text strong style={{ fontSize: '13px' }}>
                        Source: {bulletin.sourceName || bulletin.sourceId}
                      </Text>
                    )}
                    
                    {bulletin.category && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Category: {bulletin.category}
                      </Text>
                    )}
                    
                    {bulletin.message && (
                      <Paragraph 
                        style={{ 
                          margin: 0, 
                          fontSize: '13px',
                          wordBreak: 'break-word'
                        }}
                        ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                      >
                        {bulletin.message}
                      </Paragraph>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#8c8c8c'
          }}>
            <InfoCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <div>No bulletins or errors found</div>
          </div>
        )}
      </div>
    </Space>
  );
};

export default Bulletins;
